import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Chip,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import { CloudUpload, Delete, Image as ImageIcon, AddPhotoAlternate, Search } from '@mui/icons-material';
import { ImagemRelatorio, OrientacaoRelatorio } from '../types';
import { imagemRelatorioService } from '../services/imagemRelatorioService';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';

const ORIENTACOES: OrientacaoRelatorio[] = ['RETRATO', 'PAISAGEM'];

const LABEL_ORIENTACAO: Record<OrientacaoRelatorio, string> = {
  RETRATO: 'Retrato (A4)',
  PAISAGEM: 'Paisagem (A4)',
};

const TAMANHO_MAXIMO = 5 * 1024 * 1024;

const detectarTipo = (base64: string): string => {
  if (base64.startsWith('iVBOR')) return 'image/png';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/png';
};

const base64ParaUrl = (base64: string): string | null => {
  try {
    const binario = atob(base64);
    const bytes = new Uint8Array(binario.length);
    for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: detectarTipo(base64) }));
  } catch {
    return null;
  }
};

const formatarTamanho = (bytes?: number) => {
  const valor = bytes ?? 0;
  if (valor < 1024) return `${valor} B`;
  if (valor < 1024 * 1024) return `${(valor / 1024).toFixed(1)} KB`;
  return `${(valor / (1024 * 1024)).toFixed(2)} MB`;
};

const Imagens: React.FC = () => {
  const [imagens, setImagens] = useState<ImagemRelatorio[]>([]);
  const [previews, setPreviews] = useState<Partial<Record<OrientacaoRelatorio, string>>>({});
  const [erro, setErro] = useState('');
  const [uploadTarget, setUploadTarget] = useState<OrientacaoRelatorio | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [imgBase64, setImgBase64] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; orientacao: OrientacaoRelatorio | null }>({
    open: false,
    orientacao: null,
  });
  const [filtro, setFiltro] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { hasRole } = useAuth();
  const podeEditar = hasRole('ADMIN');

  useEffect(() => {
    loadImagens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urls: string[] = [];
    const novos: Partial<Record<OrientacaoRelatorio, string>> = {};
    imagens.forEach((imagem) => {
      if (imagem.img) {
        const url = base64ParaUrl(imagem.img);
        if (url) {
          novos[imagem.orientacao] = url;
          urls.push(url);
        }
      }
    });
    setPreviews(novos);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [imagens]);

  const loadImagens = async () => {
    try {
      const data = await imagemRelatorioService.listar();
      setImagens(data);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const resetUpload = () => {
    setFilePreview(null);
    setImgBase64(null);
    setDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openUpload = (orientacao: OrientacaoRelatorio) => {
    setErro('');
    resetUpload();
    setUploadTarget(orientacao);
  };

  const closeUpload = () => {
    resetUpload();
    setUploadTarget(null);
  };

  const processFile = (file: File) => {
    setErro('');
    if (!file.type.startsWith('image/')) {
      setErro('Por favor, selecione um arquivo de imagem válido.');
      return;
    }
    if (file.size > TAMANHO_MAXIMO) {
      setErro('A imagem deve ter no máximo 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const partes = dataUrl.split(',');
      setImgBase64(partes.length > 1 ? partes[1] : dataUrl);
      setFilePreview(dataUrl);
    };
    reader.onerror = () => setErro('Erro ao ler o arquivo de imagem.');
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleSalvar = async () => {
    if (!uploadTarget || !imgBase64) {
      setErro('Selecione uma imagem para enviar.');
      return;
    }
    setSalvando(true);
    try {
      await imagemRelatorioService.salvar(uploadTarget, imgBase64);
      await loadImagens();
      closeUpload();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.orientacao) return;
    try {
      await imagemRelatorioService.remover(confirmDelete.orientacao);
      await loadImagens();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setConfirmDelete({ open: false, orientacao: null });
    }
  };

  const getImagem = (orientacao: OrientacaoRelatorio) =>
    imagens.find((i) => i.orientacao === orientacao);

  const termo = filtro.trim().toLowerCase();
  const imagensFiltradas = termo
    ? imagens.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : imagens;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4">Imagens de Relatório</Typography>
          <Typography variant="body2" color="text.secondary">
            Imagens de fundo (papel timbrado) usadas nos relatórios em PDF, uma por orientação de página.
          </Typography>
        </Box>
        <TextField
          size="small"
          label="Pesquisar"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
      </Box>

      {erro && !uploadTarget && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Grid container spacing={3}>
        {ORIENTACOES.map((orientacao) => {
          const imagem = getImagem(orientacao);
          const configurada = !!imagem?.img;
          const preview = previews[orientacao];
          return (
            <Grid item xs={12} md={6} key={orientacao}>
              <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6">{LABEL_ORIENTACAO[orientacao]}</Typography>
                  <Chip
                    size="small"
                    label={configurada ? 'Configurada' : 'Não configurada'}
                    color={configurada ? 'success' : 'default'}
                  />
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    minHeight: 220,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    mb: 2,
                  }}
                >
                  {preview ? (
                    <Box
                      component="img"
                      src={preview}
                      alt={`Imagem ${LABEL_ORIENTACAO[orientacao]}`}
                      sx={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }}
                    />
                  ) : (
                    <Box textAlign="center" color="text.secondary">
                      <ImageIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                      <Typography variant="body2">Nenhuma imagem configurada</Typography>
                    </Box>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  Tamanho: {formatarTamanho(imagem?.tamanhoBytes)}
                </Typography>
                {imagem?.atualizadoEm && (
                  <Typography variant="caption" color="text.secondary">
                    Atualizada em {new Date(imagem.atualizadoEm).toLocaleString('pt-BR')}
                  </Typography>
                )}

                <Box sx={{ flexGrow: 1 }} />

                {podeEditar && (
                  <Box display="flex" gap={1} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={configurada ? <CloudUpload /> : <AddPhotoAlternate />}
                      onClick={() => openUpload(orientacao)}
                    >
                      {configurada ? 'Substituir' : 'Enviar imagem'}
                    </Button>
                    {configurada && (
                      <Button
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setConfirmDelete({ open: true, orientacao })}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={!!uploadTarget} onClose={closeUpload} maxWidth="sm" fullWidth>
        <DialogTitle>
          {uploadTarget ? `Imagem — ${LABEL_ORIENTACAO[uploadTarget]}` : 'Enviar imagem'}
        </DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <Box
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
            }}
            sx={{
              mt: 1,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'divider',
              bgcolor: dragActive ? 'action.hover' : 'grey.50',
              transition: 'all 0.2s',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
            {filePreview ? (
              <Box>
                <Box
                  component="img"
                  src={filePreview}
                  alt="Pré-visualização"
                  sx={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Clique ou arraste para trocar a imagem
                </Typography>
              </Box>
            ) : (
              <Box color="text.secondary">
                <CloudUpload sx={{ fontSize: 44, opacity: 0.6 }} />
                <Typography variant="body1">Clique para selecionar ou arraste a imagem aqui</Typography>
                <Typography variant="caption">PNG, JPG, GIF ou WEBP — até 5MB</Typography>
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            A imagem é usada como fundo de página, respeitando as margens configuradas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUpload}>Cancelar</Button>
          <Button onClick={handleSalvar} variant="contained" disabled={salvando || !imgBase64}>
            {salvando ? 'Enviando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Remover imagem"
        message="Tem certeza que deseja remover esta imagem? Esta ação não pode ser desfeita."
        confirmText="Remover"
        severity="error"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, orientacao: null })}
      />
    </Container>
  );
};

export default Imagens;
